/**
 * Bestmalz (BESTMALZ / Palatia Malz) — Heidelberg, Germany.
 *
 * Colour is published in EBC; the °L figure alongside it is the conversion
 * `°L = (EBC + 1.2) / 2.65` rounded to one decimal, not a second measurement.
 * Diastatic power is published in Windisch-Kolbach and converted with
 * `°Lintner = (°WK + 16) / 3.5`, so the figures read low against a US 2-row.
 *
 * Every row here is read off the maker's own Malt Catalog, whose per-product
 * specification tables publish extract as a **minimum**, moisture as a
 * **maximum** and colour as a band. Each entry carries the published minimum,
 * the published maximum and the mid-point of the band respectively — the
 * mid-point because it is the only figure in a range that is not a bound, and a
 * bound is the wrong number to design a recipe against. Where the maker prints
 * a °L column of its own the derived figure agrees with it, which is the check
 * that the conversion above is the one they used.
 *
 * The first ten rows predate that reading and disagreed with it, quietly and in
 * every direction: BEST Pilsen was 81 % extract against a published 80.5,
 * BEST Munich Dark 36 °Lintner against 230 °WK — 70 — and BEST Red X 19 against
 * a malt the maker sells as self-converting at 200 °WK. Half of them were marked
 * `representative` while carrying figures the maker does in fact publish. They
 * have been brought onto the same source as the rest, which moved most of the
 * numbers a little and three of them a lot. A recipe that already holds one of
 * these keeps its own snapshot, so nothing moves underneath a brewer; what
 * changes is what the next recipe gets.
 *
 * One name changed with them. There is no "BEST Wheat Malt Light" in the range —
 * the pale grade is BEST Heidelberg Wheat Malt and this row is the middle one,
 * BEST Wheat Malt. The id keeps its old spelling on purpose: ids are the public
 * contract recipes store, and a stale one is cheaper than a recycled one.
 *
 * Three entries are `representative` for one field each, and it is worth saying
 * which: `BEST Roasted Barley` publishes no extract, and `BEST Spelt Malt` and
 * `BEST Rye Malt` publish no diastatic power. A malt that can carry a grist has
 * to state one, so those two take the class-typical figures this package already
 * carries for spelt and rye (`malted-spelt`, `weyermann-rye`) rather than a
 * number invented here. Everything else on all three rows is the maker's.
 */

import type { Fermentable } from '../../schema/fermentable';

/** Bestmalz malts. @see Fermentable */
export const bestmalzFermentables: readonly Fermentable[] = [
  {
    id: 'bestmalz-pilsen',
    name: 'BEST Pilsen',
    maker: 'Bestmalz',
    origin: 'Germany',
    type: 'base',
    maltClass: 'base-pilsner',
    extractFineDryPercent: 80.5,
    moisturePercent: 4.9,
    colorLovibond: 1.9,
    colorEbc: 4,
    diastaticPowerLintner: 76,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Bestmalz house pilsner malt: pale, well modified and enzyme-rich enough to carry adjuncts alongside it. A straightforward single-malt grist for helles, pils and Kölsch, and a neutral base under aggressive hopping.',
    tags: ['base', 'lager', 'german', 'pilsner'],
    provenance: 'spec-sheet',
  },
  {
    id: 'bestmalz-pale-ale',
    name: 'BEST Pale Ale',
    maker: 'Bestmalz',
    origin: 'Germany',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 80.5,
    moisturePercent: 4.9,
    colorLovibond: 2.7,
    colorEbc: 6,
    diastaticPowerLintner: 76,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Kilned warmer than the pilsner malt, giving a fuller, faintly biscuity base with plenty of enzyme in reserve. The default continental choice when an ale grist wants more malt body than pilsner delivers.',
    tags: ['base', 'ale', 'german', 'pale-ale'],
    provenance: 'spec-sheet',
  },
  {
    id: 'bestmalz-vienna',
    name: 'BEST Vienna',
    maker: 'Bestmalz',
    origin: 'Germany',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 80.5,
    moisturePercent: 4.9,
    colorLovibond: 3.8,
    colorEbc: 9,
    diastaticPowerLintner: 76,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Amber-kilned malt that still converts itself comfortably, so it can be the whole grist. Brings a golden-orange cast and a toasted-bread sweetness suited to Vienna lager, Märzen and festbier.',
    tags: ['base', 'kilned', 'german', 'vienna'],
    provenance: 'spec-sheet',
  },
  {
    id: 'bestmalz-munich-dark',
    name: 'BEST Munich Dark',
    maker: 'Bestmalz',
    origin: 'Germany',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 80,
    moisturePercent: 4.9,
    colorLovibond: 11,
    colorEbc: 28,
    diastaticPowerLintner: 70,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'The darker of the two Munich grades, with a deep bread-crust maltiness and just enough diastatic power to stand alone. Use it whole for dunkel and bock, or at 10–30 % to give a pale grist backbone.',
    tags: ['base', 'kilned', 'german', 'munich', 'dark'],
    provenance: 'spec-sheet',
  },
  {
    id: 'bestmalz-red-x',
    name: 'BEST Red X',
    maker: 'Bestmalz',
    origin: 'Germany',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 79,
    moisturePercent: 4.9,
    colorLovibond: 11.8,
    colorEbc: 30,
    diastaticPowerLintner: 62,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'A purpose-built single-malt red malt: it produces a genuine copper-red beer with no crystal or roasted malt in the grist. It converts itself with room to spare, so it can be the whole grist without a base malt underneath it.',
    tags: ['base', 'kilned', 'german', 'red', 'single-malt'],
    provenance: 'spec-sheet',
  },
  {
    id: 'bestmalz-wheat-malt-light',
    name: 'BEST Wheat Malt',
    maker: 'Bestmalz',
    origin: 'Germany',
    type: 'base',
    maltClass: 'wheat',
    extractFineDryPercent: 82,
    moisturePercent: 5.5,
    colorLovibond: 2.3,
    colorEbc: 4.8,
    diastaticPowerLintner: 76,
    suggestedMaxPercent: 60,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Pale German wheat malt, high in extract and protein and husk-free. Provides the soft body and durable head of a weissbier at 50–60 %, or improves foam anywhere at 5–15 %; add rice hulls at high rates.',
    tags: ['base', 'wheat', 'german', 'head-retention'],
    provenance: 'spec-sheet',
  },
  {
    id: 'bestmalz-caramel-hell',
    name: 'BEST Caramel Hell',
    maker: 'Bestmalz',
    origin: 'Germany',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 75,
    moisturePercent: 4.5,
    colorLovibond: 11.8,
    colorEbc: 30,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 50,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'The pale caramel grade, stewed to fix light crystallized sugars before kilning. Adds body and a restrained honey sweetness to helles, weissbier and pale ales without pushing the color past deep straw.',
    tags: ['crystal', 'caramel', 'german', 'light'],
    provenance: 'spec-sheet',
  },
  {
    id: 'bestmalz-caramel-munich-3',
    name: 'BEST Caramel Munich III',
    maker: 'Bestmalz',
    origin: 'Germany',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 75,
    moisturePercent: 4.5,
    colorLovibond: 62.9,
    colorEbc: 165.5,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 40,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'The darkest of the three Caramel Munich grades, leaning towards raisin, burnt sugar and a firm caramel bite. Best kept to 3–8 % in bock, dunkel and dubbel, where it deepens both color and dried-fruit depth.',
    tags: ['crystal', 'caramel', 'german', 'dark'],
    provenance: 'spec-sheet',
  },
  {
    id: 'bestmalz-chocolate',
    name: 'BEST Chocolate',
    maker: 'Bestmalz',
    origin: 'Germany',
    type: 'roasted',
    maltClass: 'roasted',
    extractFineDryPercent: 75,
    moisturePercent: 4.5,
    colorLovibond: 340.1,
    colorEbc: 900,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 10,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Barley malt roasted to a dark cocoa color, contributing chocolate and light coffee notes plus a dry finish. Typical additions run 2–6 % in porter, stout and schwarzbier; more brings noticeable roast astringency.',
    tags: ['roasted', 'german', 'chocolate', 'porter'],
    provenance: 'spec-sheet',
  },
  {
    id: 'bestmalz-acidulated',
    name: 'BEST Acidulated',
    maker: 'Bestmalz',
    origin: 'Germany',
    type: 'kilned-toasted',
    maltClass: 'acidulated',
    extractFineDryPercent: 76,
    moisturePercent: 8,
    colorLovibond: 2.5,
    colorEbc: 5.5,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 5,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Sauermalz: pilsner malt biologically soured with its own lactic bacteria, so mash pH can be corrected without adding acid from outside the grist. As a rule of thumb each 1 % of the grist drops mash pH by roughly 0.1.',
    tags: ['acidulated', 'german', 'sauermalz', 'ph'],
    provenance: 'spec-sheet',
  },
  {
    id: 'bestmalz-heidelberg',
    name: 'BEST Heidelberg',
    maker: 'Bestmalz',
    origin: 'Germany',
    type: 'base',
    maltClass: 'base-pilsner',
    extractFineDryPercent: 80.5,
    moisturePercent: 4.9,
    colorLovibond: 1.5,
    colorEbc: 2.9,
    diastaticPowerLintner: 76,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'The palest malt Bestmalz kilns, held under 2.9 EBC so it colors a beer barely at all. Fully modified and enzyme-rich, it carries a grist on its own where even a pilsner malt would be a shade too deep.',
    tags: ['base', 'lager', 'german', 'pilsner', 'pale'],
    provenance: 'spec-sheet',
  },
  {
    id: 'bestmalz-a-xl',
    name: 'BEST A-XL',
    maker: 'Bestmalz',
    origin: 'Germany',
    type: 'base',
    maltClass: 'base-pilsner',
    extractFineDryPercent: 80,
    moisturePercent: 4.9,
    colorLovibond: 1.7,
    colorEbc: 3.4,
    diastaticPowerLintner: 76,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'A pale base malt from the Alexis barley variety, selected for the protein profile that builds foam. Brewable as the whole grist, and worth reaching for when head retention is the property being chased rather than color or flavor.',
    tags: ['base', 'german', 'head-retention', 'foam', 'lager'],
    provenance: 'spec-sheet',
  },
  {
    id: 'bestmalz-munich',
    name: 'BEST Munich',
    maker: 'Bestmalz',
    origin: 'Germany',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 80.5,
    moisturePercent: 4.9,
    colorLovibond: 6.3,
    colorEbc: 15.5,
    diastaticPowerLintner: 70,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'The lighter of the two Munich grades: bread-crust malt aroma with enough diastatic power left to convert itself. Whole-grist for maerzen and dunkles, or 10-40 % anywhere a pale beer wants more malt depth.',
    tags: ['base', 'kilned', 'german', 'munich'],
    provenance: 'spec-sheet',
  },
  {
    id: 'bestmalz-melanoidin-light',
    name: 'BEST Melanoidin Light',
    maker: 'Bestmalz',
    origin: 'Germany',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 75,
    moisturePercent: 4.9,
    colorLovibond: 19.3,
    colorEbc: 50,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 70,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Kilned wet to drive Maillard chemistry, giving concentrated malt aroma and dextrins without crystal sweetness. The paler of the two grades, usable at higher rates than most color malts where a beer wants body and malt intensity together.',
    tags: ['kilned', 'german', 'melanoidin', 'malty'],
    provenance: 'spec-sheet',
  },
  {
    id: 'bestmalz-melanoidin',
    name: 'BEST Melanoidin',
    maker: 'Bestmalz',
    origin: 'Germany',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 75,
    moisturePercent: 4.9,
    colorLovibond: 27.1,
    colorEbc: 70.5,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 50,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'The darker melanoidin grade, deepening both color and the cooked-malt character that reads as a long decoction. A few percent lends a red-amber cast to alt, bock and maerzen; it dominates well before its ceiling.',
    tags: ['kilned', 'german', 'melanoidin', 'malty', 'dark'],
    provenance: 'spec-sheet',
  },
  {
    id: 'bestmalz-caramel-pils',
    name: 'BEST Caramel Pils',
    maker: 'Bestmalz',
    origin: 'Germany',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 75,
    moisturePercent: 4.5,
    colorLovibond: 2.3,
    colorEbc: 5,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 50,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'The palest caramel malt in the range, stewed for body and foam rather than for color. It thickens a pilsner or a light beer without moving the color, which is exactly what a low-alcohol grist usually needs.',
    tags: ['crystal', 'caramel', 'german', 'light', 'body'],
    provenance: 'spec-sheet',
  },
  {
    id: 'bestmalz-caramel-aromatic',
    name: 'BEST Caramel Aromatic',
    maker: 'Bestmalz',
    origin: 'Germany',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 75,
    moisturePercent: 4.5,
    colorLovibond: 19.5,
    colorEbc: 50.5,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 50,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'A mid-color caramel malt where toffee and caramel start to lead rather than accompany. It suits wheat beers, bock and porter, and it darkens a beer noticeably at rates above about 10 %.',
    tags: ['crystal', 'caramel', 'german', 'toffee'],
    provenance: 'spec-sheet',
  },
  {
    id: 'bestmalz-caramel-amber',
    name: 'BEST Caramel Amber',
    maker: 'Bestmalz',
    origin: 'Germany',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 75,
    moisturePercent: 4.5,
    colorLovibond: 27.1,
    colorEbc: 70.5,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 50,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Amber-grade caramel malt, drier and more biscuit-like than the paler grades and edging towards dried fruit. A common 5-10 % addition in maerzen, amber lager and full-bodied ales.',
    tags: ['crystal', 'caramel', 'german', 'amber', 'biscuit'],
    provenance: 'spec-sheet',
  },
  {
    id: 'bestmalz-caramel-munich-1',
    name: 'BEST Caramel Munich I',
    maker: 'Bestmalz',
    origin: 'Germany',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 75,
    moisturePercent: 4.5,
    colorLovibond: 34.6,
    colorEbc: 90.5,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 50,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'The lightest of the three Caramel Munich grades, contributing caramel sweetness and a copper cast. At 5-15 % it fills out maerzen, altbier and bock without the dried-fruit weight the darker grades bring.',
    tags: ['crystal', 'caramel', 'german', 'munich'],
    provenance: 'spec-sheet',
  },
  {
    id: 'bestmalz-caramel-munich-2',
    name: 'BEST Caramel Munich II',
    maker: 'Bestmalz',
    origin: 'Germany',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 75,
    moisturePercent: 4.5,
    colorLovibond: 45.7,
    colorEbc: 120,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 50,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'The middle Caramel Munich grade, where caramel deepens towards raisin and the color turns properly red-brown. Usually 4-10 % of a dark specialty grist, and enough on its own to set the color of a bock.',
    tags: ['crystal', 'caramel', 'german', 'munich'],
    provenance: 'spec-sheet',
  },
  {
    id: 'bestmalz-caramel-wheat',
    name: 'BEST Caramel Wheat Malt',
    maker: 'Bestmalz',
    origin: 'Germany',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 69,
    moisturePercent: 5.5,
    colorLovibond: 43.8,
    colorEbc: 115,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 30,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Caramelized wheat malt: the mouthfeel and head of wheat with the crystallized sugars of a caramel malt. Extract runs lower than the barley grades, and it is at home in weizenbock, dunkles weissbier and amber ales.',
    tags: ['crystal', 'caramel', 'german', 'wheat'],
    provenance: 'spec-sheet',
  },
  {
    id: 'bestmalz-caramel-rye',
    name: 'BEST Caramel Rye Malt',
    maker: 'Bestmalz',
    origin: 'Germany',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 75,
    moisturePercent: 5.5,
    colorLovibond: 91,
    colorEbc: 240,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 15,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Caramelized rye, bringing the grain’s pepper and its silky body at once. The published color band is wide, so treat the figure as an estimate and taste the lot; 3-10 % is plenty in a rye IPA or a multigrain ale.',
    tags: ['crystal', 'caramel', 'german', 'rye', 'spicy'],
    provenance: 'spec-sheet',
  },
  {
    id: 'bestmalz-special-x',
    name: 'BEST Special X',
    maker: 'Bestmalz',
    origin: 'Germany',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 75,
    moisturePercent: 4.5,
    colorLovibond: 132.5,
    colorEbc: 350,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 20,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'A dark caramel malt taken far enough to give burnt sugar, liquorice and dried fruit without the ash of a roasted malt. A percent or two colors and deepens an abbey ale, a porter or a schwarzbier.',
    tags: ['crystal', 'caramel', 'german', 'dark', 'burnt-sugar'],
    provenance: 'spec-sheet',
  },
  {
    id: 'bestmalz-biscuit',
    name: 'BEST Biscuit',
    maker: 'Bestmalz',
    origin: 'Germany',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 75,
    moisturePercent: 4.5,
    colorLovibond: 19.3,
    colorEbc: 50,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 20,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'The lightest malt in the roasted range: toasted rather than roasted, giving fresh bread crust and biscuit with no roast bitterness. Common at 3-10 % in maerzen, bock and amber ales for a baked-malt top note.',
    tags: ['kilned', 'toasted', 'german', 'biscuit', 'bread'],
    provenance: 'spec-sheet',
  },
  {
    id: 'bestmalz-black',
    name: 'BEST Black Malt',
    maker: 'Bestmalz',
    origin: 'Germany',
    type: 'roasted',
    maltClass: 'roasted',
    extractFineDryPercent: 65,
    moisturePercent: 4.5,
    colorLovibond: 434.4,
    colorEbc: 1150,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 5,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Roasted past chocolate to a dry, sharply roasted character that reads as coffee and burnt grain. One or two percent sets the color of a schwarzbier or a stout; much more and the astringency arrives before the flavor does.',
    tags: ['roasted', 'german', 'black', 'stout', 'porter'],
    provenance: 'spec-sheet',
  },
  {
    id: 'bestmalz-black-extra',
    name: 'BEST Black Malt eXtra',
    maker: 'Bestmalz',
    origin: 'Germany',
    type: 'roasted',
    maltClass: 'roasted',
    extractFineDryPercent: 65,
    moisturePercent: 4.5,
    colorLovibond: 509.9,
    colorEbc: 1350,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 5,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'The darkest malt Bestmalz roasts, kept for color correction more than for flavor. Half a percent moves a beer several shades, which is the point: it buys darkness at the smallest possible cost in roast bite.',
    tags: ['roasted', 'german', 'black', 'colour', 'stout'],
    provenance: 'spec-sheet',
  },
  {
    id: 'bestmalz-roasted-barley',
    name: 'BEST Roasted Barley',
    maker: 'Bestmalz',
    origin: 'Germany',
    type: 'roasted',
    maltClass: 'roasted',
    extractFineDryPercent: 65,
    moisturePercent: 5.5,
    colorLovibond: 491,
    colorEbc: 1300,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 5,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Unmalted barley roasted dark, and the grain that makes an Irish stout taste like one: dry, coffee-bitter and sharper than any roasted malt. Bestmalz publishes no extract figure for it, so the yield here is class-typical.',
    tags: ['roasted', 'german', 'barley', 'stout', 'unmalted'],
    provenance: 'representative',
  },
  {
    id: 'bestmalz-chit',
    name: 'BEST Chit Malt',
    maker: 'Bestmalz',
    origin: 'Germany',
    type: 'base',
    maltClass: 'base-pilsner',
    extractFineDryPercent: 50,
    moisturePercent: 4.9,
    colorLovibond: 1.4,
    colorEbc: 2.5,
    diastaticPowerLintner: 76,
    suggestedMaxPercent: 15,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Barley malted only far enough to chit, so it keeps the high-molecular protein and the starter enzymes a fully modified malt has spent. Brewers use it to firm up foam and to counterbalance a very well modified grist; extract is low by design, at half what a pilsner malt yields.',
    tags: ['german', 'functional', 'foam', 'protein', 'head-retention'],
    provenance: 'spec-sheet',
  },
  {
    id: 'bestmalz-smoked',
    name: 'BEST Smoked Malt',
    maker: 'Bestmalz',
    origin: 'Germany',
    type: 'kilned-toasted',
    maltClass: 'base-pale',
    extractFineDryPercent: 77,
    moisturePercent: 5.5,
    colorLovibond: 2.5,
    colorEbc: 5.5,
    diastaticPowerLintner: 76,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Pale malt smoked over beechwood from the Franconian Jura, with enough diastatic power to be the whole grist. A rauchbier takes it at 50-100 %; a few percent lends a wisp of smoke to a porter or a bock.',
    tags: ['smoked', 'german', 'beechwood', 'rauch', 'base'],
    provenance: 'spec-sheet',
  },
  {
    id: 'bestmalz-roasted-wheat-light',
    name: 'BEST Roasted Wheat Light',
    maker: 'Bestmalz',
    origin: 'Germany',
    type: 'roasted',
    maltClass: 'wheat',
    extractFineDryPercent: 76,
    moisturePercent: 8,
    colorLovibond: 1.9,
    colorEbc: 3.9,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 5,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Wheat treated in the roaster but stopped almost at once, so it adds grain sweetness and body rather than color. The lightest of the three roasted wheats, and the one that changes a pale beer least.',
    tags: ['roasted', 'german', 'wheat', 'light'],
    provenance: 'spec-sheet',
  },
  {
    id: 'bestmalz-roasted-wheat-medium',
    name: 'BEST Roasted Wheat Medium',
    maker: 'Bestmalz',
    origin: 'Germany',
    type: 'roasted',
    maltClass: 'wheat',
    extractFineDryPercent: 74,
    moisturePercent: 6,
    colorLovibond: 11.8,
    colorEbc: 30.2,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 5,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'The middle roasted wheat, carrying bread crust and light caramel into amber and dark wheat beers. The published color band is very wide, so treat the figure as a midpoint rather than a specification.',
    tags: ['roasted', 'german', 'wheat', 'amber'],
    provenance: 'spec-sheet',
  },
  {
    id: 'bestmalz-roasted-wheat-dark',
    name: 'BEST Roasted Wheat Dark',
    maker: 'Bestmalz',
    origin: 'Germany',
    type: 'roasted',
    maltClass: 'wheat',
    extractFineDryPercent: 73,
    moisturePercent: 4,
    colorLovibond: 58.9,
    colorEbc: 155,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 5,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'The darkest roasted wheat, husk-free and so noticeably smoother than a roasted barley malt at the same color. Suited to dunkles weissbier and weizenbock, where roast has to arrive without astringency.',
    tags: ['roasted', 'german', 'wheat', 'dark'],
    provenance: 'spec-sheet',
  },
  {
    id: 'bestmalz-heidelberg-wheat',
    name: 'BEST Heidelberg Wheat Malt',
    maker: 'Bestmalz',
    origin: 'Germany',
    type: 'base',
    maltClass: 'wheat',
    extractFineDryPercent: 82,
    moisturePercent: 5.5,
    colorLovibond: 1.6,
    colorEbc: 3,
    diastaticPowerLintner: 76,
    suggestedMaxPercent: 60,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'The palest wheat malt in the range, kilned for beers that have to stay bright. High extract and high protein, so it builds the body and the head of a weissbier while leaving the color alone.',
    tags: ['base', 'wheat', 'german', 'pale', 'weissbier'],
    provenance: 'spec-sheet',
  },
  {
    id: 'bestmalz-wheat-malt-dark',
    name: 'BEST Wheat Malt Dark',
    maker: 'Bestmalz',
    origin: 'Germany',
    type: 'kilned-toasted',
    maltClass: 'wheat',
    extractFineDryPercent: 82,
    moisturePercent: 5.5,
    colorLovibond: 7.2,
    colorEbc: 18,
    diastaticPowerLintner: 76,
    suggestedMaxPercent: 60,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Wheat malt kilned to amber, giving a dunkles weissbier its color and its bread-crust depth from wheat rather than from Munich malt. Converts itself, so it can carry a large share of the grist.',
    tags: ['wheat', 'kilned', 'german', 'dark', 'weissbier'],
    provenance: 'spec-sheet',
  },
  {
    id: 'bestmalz-spelt',
    name: 'BEST Spelt Malt',
    maker: 'Bestmalz',
    origin: 'Germany',
    type: 'base',
    maltClass: 'wheat',
    extractFineDryPercent: 82,
    moisturePercent: 5.5,
    colorLovibond: 2.3,
    colorEbc: 4.8,
    diastaticPowerLintner: 65,
    suggestedMaxPercent: 60,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Malted spelt, an old wheat species that ferments cleanly and drinks softer and nuttier than wheat itself. High in extract and protein; add rice hulls at the rates it is worth using, because spelt has no husk of its own.',
    tags: ['spelt', 'german', 'ancient-grain', 'pale'],
    provenance: 'representative',
  },
  {
    id: 'bestmalz-rye',
    name: 'BEST Rye Malt',
    maker: 'Bestmalz',
    origin: 'Germany',
    type: 'base',
    maltClass: 'wheat',
    extractFineDryPercent: 84.9,
    moisturePercent: 5,
    colorLovibond: 3.3,
    colorEbc: 7.5,
    diastaticPowerLintner: 30,
    suggestedMaxPercent: 50,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Malted rye, the highest-extract grain Bestmalz sells and the one that most changes a beer’s texture. Pepper and a slick, oily body arrive from about 10 %; it is husk-free and gums a mash, so rice hulls are not optional at high rates.',
    tags: ['rye', 'german', 'spicy', 'pale'],
    provenance: 'representative',
  },
  {
    id: 'bestmalz-oat',
    name: 'BEST Oat Malt',
    maker: 'Bestmalz',
    origin: 'Germany',
    type: 'adjunct-grain',
    maltClass: 'wheat',
    extractFineDryPercent: 79.3,
    moisturePercent: 4.4,
    colorLovibond: 2.5,
    colorEbc: 5.5,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 50,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Malted oats rather than flaked, so they bring their own enzymes along with the silky body oats are used for. Standard in hazy IPA and oatmeal stout at 5-20 %; the beta-glucan is real, so plan the lauter for it.',
    tags: ['oat', 'german', 'smooth', 'body', 'hazy'],
    provenance: 'spec-sheet',
  },
];
